import { useMutation, useQuery } from '@apollo/client'
import {
  Wave,
  WavesQueryVariables
} from '../../../data/gql-gen/dashboard/graphql'
import { LoggerErrorType } from '../../../data/logger'
import {
  CREATE_WAVE,
  DUPLICATE_WAVE,
  RENAME_WAVE,
  WaveMutationData
} from '../../../modules/Project/Project.mutations'
import { WAVES, WavesData } from '../../../modules/Project/Project.query'
import { captureApolloError } from '../../../utils/HelperFunctions'

export const useWaves = (projectId: string) => {
  const { data, loading, error, refetch } = useQuery<
    WavesData,
    WavesQueryVariables
  >(WAVES, {
    context: { clientName: 'dashboard' },
    variables: { projectId },
    onError: (error) => {
      captureApolloError(LoggerErrorType.ApolloQuery, 'waves', error)
    }
  })

  return { waves: data?.waves ?? [], loading, error, refetch }
}

export const useCreateWave = (projectId: string) => {
  const [createWaveMutation, { loading }] = useMutation<
    WaveMutationData,
    { projectId: string }
  >(CREATE_WAVE, { context: { clientName: 'dashboard' } })

  // Appended to the cache rather than refetched.
  const createWave = async (): Promise<Wave> => {
    const { data } = await createWaveMutation({
      variables: { projectId },
      update: (cache, { data: result }) => {
        const wave = result?.createWave
        if (!wave) {
          return
        }
        cache.updateQuery<WavesData, WavesQueryVariables>(
          { query: WAVES, variables: { projectId } },
          (cached) => ({ waves: [...(cached?.waves ?? []), wave] })
        )
      }
    })

    if (!data?.createWave) {
      throw new Error('No wave created')
    }

    return data.createWave
  }

  return { createWave, loading }
}

export const useDuplicateWave = (projectId: string) => {
  const [duplicateWaveMutation, { loading }] = useMutation<
    WaveMutationData,
    { projectId: string }
  >(DUPLICATE_WAVE, { context: { clientName: 'dashboard' } })

  const duplicateWave = async (): Promise<Wave> => {
    const { data } = await duplicateWaveMutation({
      variables: { projectId },
      update: (cache, { data: result }) => {
        const wave = result?.duplicateWave
        if (!wave) {
          return
        }
        cache.updateQuery<WavesData, WavesQueryVariables>(
          { query: WAVES, variables: { projectId } },
          (cached) => ({ waves: [...(cached?.waves ?? []), wave] })
        )
      }
    })

    if (!data?.duplicateWave) {
      throw new Error('No wave created')
    }

    return data.duplicateWave
  }

  return { duplicateWave, loading }
}

export const useRenameWave = () => {
  const [renameWaveMutation, { loading }] = useMutation<
    WaveMutationData,
    { waveId: string; label: string }
  >(RENAME_WAVE, { context: { clientName: 'dashboard' } })

  // Wave isn't normalised, so the caller refetches rather than patching here.
  const renameWave = async (waveId: string, label: string) => {
    const { data } = await renameWaveMutation({
      variables: { waveId, label }
    })

    if (!data?.renameWave) {
      throw new Error('Wave not renamed')
    }

    return data.renameWave
  }

  return { renameWave, loading }
}
